import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/brand/Logohorizontal';
import FollowUs from '@/components/tools/FollowUs';
import DownloadApp from '@/components/tools/DownloadApp';
import IconCrosssign from '@/components/icons/Crosssign';
import IconApple from '@/components/icons/Apple';
import IconCommunity from '@/components/icons/Community';
import IconFingertouch from '@/components/icons/Fingertouch';
import IconHatlesson from '@/components/icons/Hatlesson';
import IconHome from '@/components/icons/Home';
import IconLanguage from '@/components/icons/Language';
import IconMag from '@/components/icons/Mag';
import IconOnline from '@/components/icons/Online';
import IconProfile from '@/components/icons/Profile';
import IconWindows from '@/components/icons/Windows';
import IconInfo from '@/components/icons/Info';
import IconQuestiontext from '@/components/icons/Questiontext';
import IconUtilities from '@/components/icons/Utilities';
import IconWifi from '@/components/icons/IconWifi';
import BgHorizontal from '@/components/background/BgHorizontal';
import ContainerHorizontal from '@/components/tools/ContainerHorizontal';
import PaternHorizontal from '@/components/pattern/Paternhorizontal';
import Loading from '@/components/tools/Loading';
import packageInfo from '@/package.json';
import { Play } from 'lucide-react';
import { authPath, callbackFromAsPath, loginPathFromAsPath } from '@/utils/authCallback';
import { changeAppLanguage, rememberAppLanguage } from '@/i18n';
import usePublicSession from '@/hooks/usePublicSession';
import { mysteryCopy } from '@/utils/mysteryCopy';

type PublicSessionUser = {
  userId?: string | null;
  username?: string | null;
  email?: string | null;
  accessToken?: string | null;
  metadata?: {
    image?: string | null;
    badges?: Array<{ name?: string; color?: string }>;
  } | null;
};

function PublicProfileButton({ user }: { user: PublicSessionUser }) {
  const image = typeof user.metadata?.image === 'string' ? user.metadata.image : '';
  const badge = user.metadata?.badges?.[0];
  return (
    <Link
      href='/profile'
      prefetch={false}
      aria-label={user.username || user.email || 'Profile'}
      title={user.username || user.email || 'Profile'}
      className='relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 transition hover:border-primary/60 hover:bg-white/15'
    >
      {image
        ? <Image src={image} alt='' width={36} height={36} unoptimized={image.startsWith('data:')} className='h-9 w-9 rounded-full object-cover' />
        : <IconProfile className='h-6 w-6' aria-hidden />}
      {badge?.name && !badge.name.includes('data:image') && (
        <span className='absolute -right-1 -top-1 max-w-6 truncate text-xs' style={{ color: badge.color || '#fff' }} aria-hidden>{badge.name}</span>
      )}
    </Link>
  );
}

const publicLanguages = [
  ['en', 'English', '🇬🇧'], ['de', 'Deutsch', '🇩🇪'], ['fr', 'Français', '🇫🇷'],
  ['es', 'Español', '🇪🇸'], ['es-ar', 'Español (Argentina)', '🇦🇷'], ['pt-br', 'Português (Brasil)', '🇧🇷'],
  ['tr', 'Türkçe', '🇹🇷'], ['zh-cn', '简体中文', '🇨🇳'], ['ja', '日本語', '🇯🇵'],
  ['ko', '한국어', '🇰🇷'], ['id', 'Bahasa Indonesia', '🇮🇩'], ['sv', 'Svenska', '🇸🇪'],
  ['no', 'Norsk', '🇳🇴'], ['ru', 'Русский', '🇷🇺'], ['hi', 'हिन्दी', '🇮🇳'],
] as const;

function PublicLanguageSwitcher() {
  const router = useRouter();
  const active = router.locale || router.defaultLocale || 'en';
  const onChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const language = event.target.value;
    rememberAppLanguage(language, router.locales);
    await changeAppLanguage(language);
    await router.push({ pathname: router.pathname, query: router.query }, router.asPath, { locale: language, scroll: false });
  };
  return (
    <select
      value={active}
      onChange={onChange}
      aria-label='Language'
      className='max-w-40 cursor-pointer bg-transparent text-sm text-white outline-none [&>option]:bg-black [&>option]:text-white'
    >
      {publicLanguages.map(([code, label, flag]) => <option key={code} value={code}>{flag} {label}</option>)}
    </select>
  );
}

const publicLinks = [
  { href: '/', key: 'header.home', Icon: IconHome },
  { href: '/classroom', key: 'header.classroom', Icon: IconHatlesson },
  { href: '/clubs', key: 'header.clubs', Icon: IconCommunity },
  { href: '/mag', key: 'header.mag', Icon: IconMag },
];

function AuthButtons() {
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <div className="flex items-center gap-1">
      <button type='button' onClick={() => router.push(authPath('/auth/register', callbackFromAsPath(router.asPath) || '/'))} className="hidden h-10 whitespace-nowrap rounded-2xl bg-primary px-4 text-sm font-bold sm:block md:px-6" aria-label="Sign up">
        <p>{t('header.sign_up')}</p>
      </button>
      <button type='button' onClick={() => router.push(loginPathFromAsPath(router.asPath))} className="flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-transparent px-4 text-sm font-bold sm:bg-primary/20 md:px-6" aria-label="Login">
        <p>{t('header.login')}</p><IconFingertouch className="h-6 w-6" aria-hidden />
      </button>
    </div>
  );
}

function SupportButton() {
  return (
    <button type='button' onClick={() => document.getElementById('support-button')?.click()} className="relative flex items-center justify-center bg-transparent p-0" aria-label="Chat support">
      <IconOnline className="h-6 w-6" aria-hidden />
      <span className="absolute top-4 right-2 h-2 w-2 rounded-full bg-green-500" aria-hidden />
    </button>
  );
}

export function PublicHeader() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, loading } = usePublicSession();
  return (
    <header className="bg-white/10 text-white shadow-md backdrop-blur w-11/12 mx-auto rounded-2xl sticky top-2 z-10">
      <div className="flex justify-between items-center bg-white/10 p-4 rounded-2xl w-full gap-4 overflow-x-auto">
        <Logo />
        <nav className="flex gap-0 xl:gap-4 flex-1">
          {publicLinks.map(({ href, key, Icon }) => (
            <Link key={href} href={href} prefetch={false} className={`flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-4 py-1 text-sm font-bold ${router.pathname === href ? 'bg-primary' : ''}`}>
              <Icon className="h-6 w-6" aria-hidden /><span>{t(key)}</span>
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-4 z-10">
          {loading
            ? <div className="h-10 w-10" aria-hidden />
            : user
              ? <PublicProfileButton user={user} />
              : <AuthButtons />}
          <div className="h-8 w-1 bg-white/10 rounded-2xl" />
          <SupportButton />
        </div>
      </div>
      <div className="flex items-center justify-between py-2 px-4">
        <div className="flex items-center gap-4 text-xs text-white-sec">
          <div className="flex items-center gap-2"><IconWindows className="w-6 h-6" /><p>{t('common.ctrl_d')}</p></div>
          <div className="flex items-center gap-2"><IconApple className="w-6 h-6" /><p>{t('common.cmd_d')}</p></div>
          <div className="h-6 w-0.5 bg-white/10 rounded-2xl" /><p>{t('header.add_bookmarks')}</p>
        </div>
        <DownloadApp />
      </div>
    </header>
  );
}

export function PublicMiniHeader({ glass = false }: { glass?: boolean }) {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, loading } = usePublicSession();
  return <div className={(glass ? 'border border-white/20 bg-black/15 backdrop-blur-xl backdrop-saturate-[0.9] shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_8px_24px_rgba(0,0,0,0.18)] px-3 py-1.5 rounded-full' : 'bg-primary/60 sm:bg-black/50 px-4 py-2 rounded-2xl') + ' relative overflow-hidden w-fit flex fade-in-top'}>
    <div className={`flex items-center z-10 ${glass ? 'gap-3' : 'gap-4'}`}>
      <SupportButton />
      {loading
        ? <div className="h-8 w-8" aria-hidden />
        : user
          ? <PublicProfileButton user={user} />
          : <div className="flex items-center gap-1">
              <button type='button' onClick={() => router.push(authPath('/auth/register', callbackFromAsPath(router.asPath) || '/'))} className="hidden rounded-xl bg-primary px-3 py-2 text-sm font-bold sm:block" aria-label="Sign up"><p>{t('header.sign_up')}</p></button>
              <button type='button' onClick={() => router.push(loginPathFromAsPath(router.asPath))} className='flex items-center gap-2 rounded-xl px-2 py-1 text-sm font-bold' aria-label="Login"><p>{t('header.login')}</p><IconFingertouch className="h-6 w-6" /></button>
            </div>}
    </div>
    {!glass && <BgHorizontal disableShadow className="w-full h-40" />}
  </div>;
}

export function PublicMobileMenu({ isHeader }: { isHeader?: boolean }) {
  const { t } = useTranslation();
  const router = useRouter();
  return (
    <div>
      {isHeader && <ContainerHorizontal className="bg-black/5 backdrop-blur-lg rounded-t-3xl gap-8 py-1 px-4 sm:px-16 pb-10 -mb-9">
        <Link href="/mag" prefetch={false} className="flex items-center gap-2 text-sm font-bold hover:text-primary"><IconMag className="h-6 w-6" /><p>{t('header.mag')}</p></Link>
        <DownloadApp />
      </ContainerHorizontal>}
      <div className={(isHeader ? 'bg-black' : 'bg-black/80') + ' border-t-2 border-white/10 flex flex-row items-center justify-between px-4 sm:px-16 py-1 backdrop-blur-lg relative rounded-t-3xl overflow-hidden'}>
        <Link href="/" prefetch={false} className="flex flex-col items-center justify-center gap-1 text-sm font-bold hover:text-primary"><span className="flex h-8 items-center"><IconHome className="h-6 w-6" /></span><span>{t('header.home')}</span></Link>
        <Link href="/classroom" prefetch={false} className="flex flex-col items-center justify-center gap-1 text-sm font-bold hover:text-primary"><span className="flex h-8 items-center"><IconHatlesson className="h-6 w-6" /></span><span>{t('header.classroom')}</span></Link>
        <Link href="/classroom" prefetch={false} className="flex flex-col items-center justify-center gap-1 text-sm font-bold hover:text-primary" aria-label={t('menu.start')}>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white"><Play className="h-4 w-4 translate-x-px fill-current rtl:-scale-x-100" /></span><span>{t('menu.start')}<span className="sr-only"> — {t('landing.start_learning_right_way')}</span></span>
        </Link>
        <button type='button' className="flex flex-col items-center justify-center gap-1 text-sm font-bold hover:text-primary" onClick={() => router.push('/profile')} aria-label="You"><span className="flex h-8 items-center"><IconProfile className="h-6 w-6" /></span><span>{t('menu.you')}</span></button>
        <div className="absolute top-0 left-0 w-full -z-10"><PaternHorizontal className="w-full h-screen" /></div>
      </div>
    </div>
  );
}

export function PublicGetStarted() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 10000);
    return () => window.clearTimeout(timer);
  }, []);
  if (!visible) return null;
  return (
    <div className="from-primary-sec to-primary bg-gradient-to-r rounded-2xl flex items-center p-2 lg:px-4 fade-in-bottom w-11/12 mx-auto">
      <button type='button' onClick={() => setVisible(false)} className='flex items-center justify-center rounded-xl p-1' aria-label="Close"><IconCrosssign className="h-6 w-6" /></button>
      <p className="mx-2 flex-1">{t('common.ready_to_get_started')}</p>
      <Link className="rounded-xl bg-white px-4 py-2 font-bold text-black" href="/auth/register" prefetch={false}>{t('common.get_started')}</Link>
    </div>
  );
}

export function PublicFooter() {
  const { t } = useTranslation();
  const router = useRouter();
  const mystery = mysteryCopy(router.locale);
  const [isOnline, setIsOnline] = useState(true);
  useEffect(() => {
    setIsOnline(navigator.onLine);
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online); window.addEventListener('offline', offline);
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offline); };
  }, []);
  return (
    <footer className="text-white p-4 border-white/10 border-t-2 space-y-4 pb-[10rem] lg:pb-0">
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:!grid-cols-5 p-2 lg:p-4 gap-4">
        <div className="space-y-4 col-span-1 xs:col-span-2 lg:!col-span-1"><Logo /><p className="text-xs text-white-mid">{t('footer.description')}</p></div>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 col-span-1 xs:col-span-2 lg:!col-span-1">
          {publicLinks.map(({ href, key, Icon }) => <Link key={href} href={href} prefetch={false} className="flex items-center justify-start gap-2 text-sm hover:text-primary" aria-label={t(key)}><Icon className="h-6 w-6" /><p>{t(key)}</p></Link>)}
          <Link href='/help' prefetch={false} className="flex items-center justify-start gap-2 text-sm hover:text-primary" aria-label={t('footer.help_center', { defaultValue: 'Help Center' })}><IconQuestiontext className="h-6 w-6" /><p>{t('footer.help_center', { defaultValue: 'Help Center' })}</p></Link>
        </div>
        <div className="flex flex-col gap-2 text-sm items-start">
          <div className="flex items-center gap-1"><IconUtilities className="h-6 w-6" /><p>{t('footer.facilities')}</p></div>
          <Link href="/courses" prefetch={false}>{t('header.courses')}</Link><Link href="/pricing" prefetch={false}>{t('footer.packages_pricing')}</Link>
          <Link href="/#leitner-system" prefetch={false}>{t('footer.leitner_system')}</Link><Link href="/clubs" prefetch={false}>mynawoo.com/clubs</Link>
          <Link href="/#unique-features" prefetch={false}>{t('footer.ai_system')}</Link><Link href="/#cefr-progression" prefetch={false}>{t('footer.cefr_progression')}</Link>
          <Link href="/language-learning-games" prefetch={false}>{mystery.nav}</Link>
        </div>
        <div className="flex flex-col gap-2 text-sm items-start">
          <div className="flex items-center gap-1"><IconInfo className="h-6 w-6" /><p>{t('footer.about')}</p></div>
          <Link href="/terms-of-use" prefetch={false}>{t('footer.terms_of_use')}</Link><Link href="/privacy-policy" prefetch={false}>{t('footer.privacy_policy')}</Link><Link href="/refund-policy" prefetch={false}>{t('footer.refund_policy', { defaultValue: 'Refund Policy' })}</Link><Link href="/contact" prefetch={false}>{t('footer.contact')}</Link><Link href="/faq" prefetch={false}>{t('footer.frequently_asked_questions')}</Link><Link href="/help" prefetch={false}>{t('footer.help_center', { defaultValue: 'Help Center' })}</Link>
          <Link href="https://buymeacoffee.com/mynawoo" target="_blank" rel="noopener noreferrer">{t('footer.buy_me_coffee')}</Link>
        </div>
        <div className="space-y-4"><div className="flex items-center gap-2"><SupportButton /><p>{t('footer.chat_support')}</p></div><div className="flex items-center gap-2"><IconLanguage className="h-6 w-6" /><PublicLanguageSwitcher /></div></div>
      </div>
      <div className="h-0.5 w-full lg:w-[70%] mx-auto bg-white/10 rounded-full" /><div className="mx-auto w-full lg:w-fit"><FollowUs /></div><div className="h-0.5 w-full lg:w-[70%] mx-auto bg-white/10 rounded-full" />
      <div className="flex flex-col-reverse sm:flex-row items-start sm:items-center justify-between text-sm">
        <div className={(isOnline ? 'text-green-500' : 'text-yellow-600 animate-pulse') + ' flex items-center gap-1'}><IconWifi className="h-4 w-4" /><p>{isOnline ? t('footer.stable_connection') : t('footer.unstable_connection')}</p>{!isOnline && <Loading />}</div>
        <div className="flex flex-col lg:flex-row items-start sm:items-center gap-2 text-white-mid"><p>{t('common.version')} {packageInfo.version}</p><p>{t('common.all_rights_reserved', { year: new Date().getFullYear() })}</p></div><DownloadApp />
      </div>
    </footer>
  );
}
